// ─────────────────────────────────────────────────────────────────────────────
// Excel de Líderes — las 3 hojas del reporte, con la forma visual de la plantilla del cliente
// `06 - Lideres.xlsx` (auditoría 2026-09-04): paleta azul/amarilla propia (`excelColoresLideres`),
// banner + banda amarilla + bloque de metadatos, encabezado de tabla azul con «Participación»
// destacada en amarillo, color por COLUMNA en las filas de datos (no banding zebra) y rejilla de
// bordes finos.
//
// EL TEXTO COMPLETO SALE DE i18n (`reports.leaders.*`), en los 7 idiomas: nombres de hoja, banner,
// metadatos, encabezados, el «Detalle» de cada KPI y el glosario de «Definiciones». El libro se
// descarga en el idioma de quien lo exporta, igual que ya hacía el Excel Demográfico
// (`DemograficoExportButton.tsx`) — un archivo que se comparte fuera de la app tiene que poder
// leerse sin saber español.
//
// Las etiquetas de dato se reusan de las claves que YA existen y que la pantalla también usa
// (`col*`, `kpi*`): así la columna del Excel y la de la tabla dicen lo mismo, y no hay dos
// traducciones del mismo concepto que puedan divergir. En particular NO se abrevia
// «Reconocimientos» como «Recos», como sí hace la plantilla: se retiró en UAT porque no se
// entendía, y hay una guarda en `recognitionLocales.test.ts` que lo impide.
//
// Vive fuera de `LeadersTab.tsx` para que la vista se quede con la pantalla y no con 200 líneas de
// especificación de estilos. `styleHints.dataColumnStyles` es lo que mantiene el costo en
// O(columnas): la hoja de equipos puede traer 1 600+ líderes y declarar el estilo celda a celda
// serían decenas de miles de objetos por descarga.
//
// Diferencias A PROPÓSITO frente a la plantilla de referencia:
//   1. Los textos de «Tamaño de equipo» describen el equipo DIRECTO (reportes inmediatos), que es lo
//      que calcula el backend. La plantilla dice "directa o indirectamente" y titula "Cascadeo
//      Jerárquico": copiar esa redacción documentaría un cálculo que el sistema no hace.
//   2. Sin el pie "Datos de ejemplo del entorno demo… no representan producción": esa leyenda
//      describe el archivo de MUESTRA del cliente, no un reporte con datos reales (mismo criterio ya
//      aplicado en la Reportería de Moderación).
//   3. Los anchos de «OU» y «Nivel» van intercambiados respecto a la plantilla, que dejó 11.44 a la
//      OU (un nombre de OU no cabe) y 30.44 a «Nivel» (que solo pinta "N-3"). Son los dos anchos
//      medidos, aplicados a la columna que sí los necesita.
// ─────────────────────────────────────────────────────────────────────────────
import type { LeaderAggregateRowDto, LeadersListKpiDto } from '@/api/recognition';
import { excelColoresLideres } from '@/utils/excelTheme';
import { mergeDeFila } from '@/utils/exportExcelMultiSheet';
import type {
  ExcelCellStyleHint, ExcelCellValue, ExcelColumnStyleHint, ExcelSheetSpec,
} from '@/utils/exportExcelMultiSheet';

const SIN_DATO = '—';

const FORMATO_ENTERO = '#,##0';
const FORMATO_RATIO = '0.00';
const FORMATO_PORCENTAJE = '0.0%';

/** Índice de la primera columna numérica de la hoja de equipos (las 4 anteriores son texto). */
const PRIMERA_COLUMNA_NUMERICA = 4;
/** Índice de la columna «Participación», la única destacada en amarillo. */
const COLUMNA_PARTICIPACION = 9;
const TOTAL_COLUMNAS_EQUIPOS = COLUMNA_PARTICIPACION + 1;

/**
 * Anchos de la hoja de equipos. Los cuatro rótulos de reconocimientos son los más largos de todos
 * (y crecen todavía más en alemán y neerlandés): a 20 caracteres el encabezado se reparte en dos o
 * tres renglones dentro de la celda en vez de desbordarse sobre la columna vecina. Ninguna fila
 * lleva alto explícito a propósito — con «Ajustar texto» puesto, Excel calcula el alto que cada
 * encabezado necesita en el idioma que toque, y un alto fijo cortaría el más largo.
 */
const ANCHOS_EQUIPOS = [28.55, 27.55, 30.44, 11.44, 16, 20, 20, 20, 20, 14];

/**
 * Resuelve un texto del namespace `recognition` bajo `reports.leaders`. Se recibe como parámetro y
 * no se importa el singleton de i18n: así el módulo no depende de él y una prueba puede pasar su
 * propio traductor.
 */
type Traductor = (clave: string) => string;

/** Banner de una hoja: título en azul oscuro, subtítulo en azul claro y banda amarilla debajo. */
function estilosDeBanner(anchoEnColumnas: number): {
  cellStyles: ExcelCellStyleHint[];
  merges: string[];
  bandRows: Array<{ row: number; fillArgb: string; columnCount: number }>;
} {
  return {
    cellStyles: [
      {
        row: 0,
        column: 0,
        fillArgb: excelColoresLideres.tituloFondo,
        fontColorArgb: excelColoresLideres.tituloTexto,
        isBold: true,
        fontSize: 18,
        horizontal: 'left',
        vertical: 'middle',
        indent: 1,
      },
      {
        row: 1,
        column: 0,
        fillArgb: excelColoresLideres.subtituloFondo,
        fontColorArgb: excelColoresLideres.subtituloTexto,
        isItalic: true,
        fontSize: 11,
        hasWrapText: true,
        horizontal: 'left',
        vertical: 'middle',
        indent: 1,
      },
    ],
    merges: [mergeDeFila(0, 0, anchoEnColumnas - 1), mergeDeFila(1, 0, anchoEnColumnas - 1)],
    bandRows: [{ row: 2, fillArgb: excelColoresLideres.bandaFondo, columnCount: anchoEnColumnas }],
  };
}

/** Encabezado de tabla: azul sólido, texto blanco centrado. `columnaDestacada` sale en amarillo. */
function estilosDeHeader(
  fila: number, totalColumnas: number, columnaDestacada?: number,
): ExcelCellStyleHint[] {
  return Array.from({ length: totalColumnas }, (_, columna): ExcelCellStyleHint => ({
    row: fila,
    column: columna,
    fillArgb: columna === columnaDestacada
      ? excelColoresLideres.headerDestacadoFondo
      : excelColoresLideres.headerTablaFondo,
    fontColorArgb: columna === columnaDestacada
      ? excelColoresLideres.headerDestacadoTexto
      : excelColoresLideres.headerTablaTexto,
    isBold: true,
    horizontal: 'center',
    vertical: 'middle',
    hasWrapText: true,
  }));
}

/** Columna de etiquetas: azul claro, negrita, alineada a la izquierda. */
function columnaDeEtiqueta(columna: number): ExcelColumnStyleHint {
  return {
    column: columna,
    fillArgb: excelColoresLideres.celdaTextoFondo,
    fontColorArgb: excelColoresLideres.textoCuerpo,
    isBold: true,
    horizontal: 'left',
    vertical: 'top',
    hasWrapText: true,
  };
}

/** Columna de prosa: blanca, sin negrita, ajustada a la celda para no desbordar el ancho. */
function columnaDeProsa(columna: number): ExcelColumnStyleHint {
  return {
    column: columna,
    fillArgb: excelColoresLideres.celdaNumeroFondo,
    fontColorArgb: excelColoresLideres.textoCuerpo,
    horizontal: 'left',
    vertical: 'top',
    hasWrapText: true,
  };
}

/**
 * Hoja "Resumen Ejecutivo". El orden de los 8 KPI es el de la plantilla y no debe reordenarse:
 * primero el conteo de líderes y el tamaño de equipo, luego los cuatro totales de reconocimientos
 * (equipo antes que líder, recibidos antes que enviados) y al final los dos indicadores derivados.
 *
 * Los valores viajan como NÚMERO con formato nativo de Excel (`#,##0` / `0.00` / `0.0%`), no como
 * texto ya formateado: un `"42.3%"` string se alineaba a la izquierda mientras los conteos
 * numéricos se iban a la derecha, y la columna «Valor» salía con los datos en dos alineaciones
 * distintas. Además así el número sigue siendo sumable y ordenable en Excel.
 */
function buildResumenSheet(
  traducir: Traductor,
  kpis: LeadersListKpiDto,
  titulo: string,
  subtitulo: string,
  periodo: string,
  filtros: string,
  generado: string,
): ExcelSheetSpec {
  const ANCHO = 3;
  const FILA_HEADER = 7;
  const banner = estilosDeBanner(ANCHO);

  const kpiRows: Array<{ label: string; valor: ExcelCellValue; detalle: string; formato: string }> = [
    {
      label: traducir('kpiTotalLeaders'),
      valor: kpis.totalLideres,
      detalle: traducir('excel.detailTotalLeaders'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('kpiTeamSizeSum'),
      valor: kpis.tamanoEquipoSuma,
      detalle: traducir('excel.detailTeamSize'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('colTeamReceived'),
      valor: kpis.recosEquipoRecibidosSuma,
      detalle: traducir('excel.detailTeamReceived'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('colTeamSent'),
      valor: kpis.recosEquipoEnviadosSuma,
      detalle: traducir('excel.detailTeamSent'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('colLeaderReceived'),
      valor: kpis.recosQueRecibeElLiderSuma,
      detalle: traducir('excel.detailLeaderReceived'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('colLeaderSent'),
      valor: kpis.recosQueEnviaElLiderSuma,
      detalle: traducir('excel.detailLeaderSent'),
      formato: FORMATO_ENTERO,
    },
    {
      label: traducir('kpiRatioAvg'),
      valor: kpis.ratioPromedio ?? SIN_DATO,
      detalle: traducir('excel.detailRatio'),
      formato: FORMATO_RATIO,
    },
    {
      label: traducir('kpiParticipationAvg'),
      valor: kpis.participacionPromedio / 100,
      detalle: traducir('excel.detailParticipation'),
      formato: FORMATO_PORCENTAJE,
    },
  ];

  const rows: ExcelCellValue[][] = [
    [titulo],
    [subtitulo],
    [],
    [traducir('excel.metaPeriod'), periodo],
    [traducir('excel.metaFilters'), filtros],
    [traducir('excel.metaGenerated'), generado],
    [],
    [traducir('excel.colKpi'), traducir('excel.colValue'), traducir('excel.colDetail')],
    ...kpiRows.map((kpi): ExcelCellValue[] => [kpi.label, kpi.valor, kpi.detalle]),
  ];

  const FILAS_META = [3, 4, 5];
  const primeraFilaKpi = FILA_HEADER + 1;
  const ultimaFilaKpi = primeraFilaKpi + kpiRows.length - 1;

  return {
    name: traducir('excel.sheetSummary'),
    rows,
    styleHints: {
      isDefaultThemeSkipped: true,
      columnWidths: [34.33, 18.11, 49.55],
      merges: [...banner.merges, ...FILAS_META.map((fila) => `B${fila + 1}:C${fila + 1}`)],
      bandRows: banner.bandRows,
      dataColumnStyles: {
        startRow: primeraFilaKpi,
        endRow: ultimaFilaKpi,
        columns: [
          columnaDeEtiqueta(0),
          {
            column: 1,
            fillArgb: excelColoresLideres.celdaNumeroFondo,
            fontColorArgb: excelColoresLideres.valorDestacado,
            isBold: true,
            horizontal: 'right',
            vertical: 'middle',
          },
          columnaDeProsa(2),
        ],
      },
      cellStyles: [
        ...banner.cellStyles,
        // Bloque de metadatos: etiqueta en amarillo sólido, valor en amarillo claro.
        ...FILAS_META.flatMap((fila): ExcelCellStyleHint[] => [
          {
            row: fila,
            column: 0,
            fillArgb: excelColoresLideres.metaEtiquetaFondo,
            fontColorArgb: excelColoresLideres.textoCuerpo,
            isBold: true,
            vertical: 'middle',
          },
          {
            row: fila,
            column: 1,
            fillArgb: excelColoresLideres.metaValorFondo,
            fontColorArgb: excelColoresLideres.textoCuerpo,
            hasWrapText: true,
            vertical: 'middle',
          },
        ]),
        ...estilosDeHeader(FILA_HEADER, ANCHO),
        // Un formato numérico por FILA: la columna «Valor» mezcla conteos, un ratio y un porcentaje,
        // así que no puede fijarse a nivel de columna.
        ...kpiRows.map((kpi, indice): ExcelCellStyleHint => (
          { row: primeraFilaKpi + indice, column: 1, numFmt: kpi.formato }
        )),
      ],
      borderedRanges: [
        {
          startRow: FILAS_META[0],
          endRow: FILAS_META[FILAS_META.length - 1],
          startColumn: 0,
          endColumn: ANCHO - 1,
          colorArgb: excelColoresLideres.bordeTabla,
        },
        {
          startRow: FILA_HEADER,
          endRow: ultimaFilaKpi,
          startColumn: 0,
          endColumn: ANCHO - 1,
          colorArgb: excelColoresLideres.bordeTabla,
        },
      ],
    },
  };
}

/**
 * Hoja "Definiciones" — el glosario de la plantilla, en su mismo orden, más «Nivel» (columna que la
 * plantilla no explica pero el reporte sí exporta).
 */
function buildDefinicionesSheet(traducir: Traductor): ExcelSheetSpec {
  const ANCHO = 3;
  const FILA_HEADER = 4;
  const banner = estilosDeBanner(ANCHO);

  /** `[clave del rótulo, clave de «Qué es», clave de «Cómo se calcula»]`. */
  const definiciones: Array<[string, string, string]> = [
    ['colTeamSize', 'excel.whatTeamSize', 'excel.howTeamSize'],
    ['colTeamReceived', 'excel.whatTeamReceived', 'excel.howTeamReceived'],
    ['colTeamSent', 'excel.whatTeamSent', 'excel.howTeamSent'],
    ['colLeaderReceived', 'excel.whatLeaderReceived', 'excel.howLeaderReceived'],
    ['colLeaderSent', 'excel.whatLeaderSent', 'excel.howLeaderSent'],
    ['colRatio', 'excel.whatRatio', 'excel.howRatio'],
    ['colParticipation', 'excel.whatParticipation', 'excel.howParticipation'],
    ['colLeader', 'excel.whatLeader', 'excel.howLeader'],
    ['colLevel', 'excel.whatLevel', 'excel.howLevel'],
  ];

  const rows: ExcelCellValue[][] = [
    [traducir('excel.definitionsTitle')],
    [traducir('excel.definitionsSubtitle')],
    [],
    [],
    [traducir('excel.colDatum'), traducir('excel.colWhatIs'), traducir('excel.colHowCalculated')],
    ...definiciones.map((claves): ExcelCellValue[] => claves.map(traducir)),
  ];

  const primeraFilaDato = FILA_HEADER + 1;
  const ultimaFilaDato = primeraFilaDato + definiciones.length - 1;

  return {
    name: traducir('excel.sheetDefinitions'),
    rows,
    styleHints: {
      isDefaultThemeSkipped: true,
      columnWidths: [36.11, 38.11, 53.33],
      merges: banner.merges,
      bandRows: banner.bandRows,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        columns: [columnaDeEtiqueta(0), columnaDeProsa(1), columnaDeProsa(2)],
      },
      cellStyles: [...banner.cellStyles, ...estilosDeHeader(FILA_HEADER, ANCHO)],
      borderedRanges: [{
        startRow: FILA_HEADER,
        endRow: ultimaFilaDato,
        startColumn: 0,
        endColumn: ANCHO - 1,
        colorArgb: excelColoresLideres.bordeTabla,
      }],
    },
  };
}

/**
 * Hoja de equipos — una fila por líder. Los conteos viajan como número con formato `#,##0` y
 * «Participación» como fracción con formato `0.0%` (no el string `"42.3%"`), para que toda la mitad
 * numérica de la tabla quede alineada a la derecha y siga siendo ordenable en Excel.
 */
function buildEquiposSheet(
  traducir: Traductor, lideres: readonly LeaderAggregateRowDto[],
): ExcelSheetSpec {
  const FILA_HEADER = 4;
  const banner = estilosDeBanner(TOTAL_COLUMNAS_EQUIPOS);

  const rows: ExcelCellValue[][] = [
    [traducir('excel.sheetTeams')],
    [traducir('excel.teamsSubtitle')],
    [],
    [],
    [
      traducir('colLeader'), traducir('colPosition'), traducir('colOu'), traducir('colLevel'),
      traducir('colTeamSize'), traducir('colTeamReceived'), traducir('colTeamSent'),
      traducir('colLeaderSent'), traducir('colLeaderReceived'), traducir('colParticipation'),
    ],
    ...lideres.map((row): ExcelCellValue[] => [
      row.liderNombre ?? row.liderId,
      row.puesto ?? '',
      row.ouNombre ?? row.ouId ?? '',
      formatNivel(row.nivel),
      row.tamanoEquipo,
      row.recosEquipoRecibidos,
      row.recosEquipoEnviados,
      row.recosQueEnviaElLider,
      row.recosQueRecibeElLider,
      row.participacionPct / 100,
    ]),
  ];

  const primeraFilaDato = FILA_HEADER + 1;
  const ultimaFilaDato = primeraFilaDato + lideres.length - 1;

  const columnasDeTexto = Array.from(
    { length: PRIMERA_COLUMNA_NUMERICA },
    (_, columna): ExcelColumnStyleHint => ({
      column: columna,
      fillArgb: excelColoresLideres.celdaTextoFondo,
      fontColorArgb: excelColoresLideres.textoCuerpo,
      horizontal: 'left',
      vertical: 'middle',
    }),
  );
  const columnasNumericas = Array.from(
    { length: COLUMNA_PARTICIPACION - PRIMERA_COLUMNA_NUMERICA },
    (_, indice): ExcelColumnStyleHint => ({
      column: PRIMERA_COLUMNA_NUMERICA + indice,
      fillArgb: excelColoresLideres.celdaNumeroFondo,
      fontColorArgb: excelColoresLideres.textoCuerpo,
      horizontal: 'right',
      vertical: 'middle',
      numFmt: FORMATO_ENTERO,
    }),
  );

  return {
    name: traducir('excel.sheetTeams'),
    rows,
    styleHints: {
      isDefaultThemeSkipped: true,
      // «OU» y «Nivel» llevan intercambiados los anchos de la plantilla — ver la nota 3 del
      // encabezado de este archivo.
      columnWidths: ANCHOS_EQUIPOS,
      merges: banner.merges,
      bandRows: banner.bandRows,
      dataColumnStyles: {
        startRow: primeraFilaDato,
        endRow: ultimaFilaDato,
        columns: [
          ...columnasDeTexto,
          ...columnasNumericas,
          {
            column: COLUMNA_PARTICIPACION,
            fillArgb: excelColoresLideres.celdaDestacadaFondo,
            fontColorArgb: excelColoresLideres.textoCuerpo,
            horizontal: 'right',
            vertical: 'middle',
            numFmt: FORMATO_PORCENTAJE,
          },
        ],
      },
      cellStyles: [
        ...banner.cellStyles,
        ...estilosDeHeader(FILA_HEADER, TOTAL_COLUMNAS_EQUIPOS, COLUMNA_PARTICIPACION),
      ],
      borderedRanges: [{
        startRow: FILA_HEADER,
        endRow: ultimaFilaDato,
        startColumn: 0,
        endColumn: COLUMNA_PARTICIPACION,
        colorArgb: excelColoresLideres.bordeTabla,
      }],
    },
  };
}

/** «N-1» = raíz del organigrama (sin manager), «N-2», «N-3»… hacia abajo — el formato de la
 *  columna «Nivel» de `06 - Lideres.xlsx`. El backend manda la profundidad cruda (`number`); el
 *  prefijo "N-" no se traduce: es una notación de organigrama, igual en los 7 idiomas, y así lo
 *  pinta también la tabla en pantalla. */
function formatNivel(nivel: number | null | undefined): string {
  return nivel != null ? `N-${nivel}` : SIN_DATO;
}

/**
 * Las 3 hojas del Excel de Líderes, en el orden del libro de referencia: Resumen Ejecutivo,
 * Definiciones y la hoja de equipos.
 *
 * `kpis` son SIEMPRE los del recorte completo, aun cuando `lideres` traiga una sola fila (export por
 * fila): son los KPI del periodo y del alcance consultado, no de la selección.
 */
export function buildLeadersExcelSheets(parametros: Readonly<{
  /** Resuelve una clave RELATIVA a `reports.leaders` del namespace `recognition`. */
  traducir: Traductor;
  kpis: LeadersListKpiDto;
  lideres: readonly LeaderAggregateRowDto[];
  titulo: string;
  subtitulo: string;
  periodo: string;
  filtros: string;
  /** Fecha y hora de generación, ya formateada en el idioma activo por el caller. */
  generado: string;
}>): ExcelSheetSpec[] {
  const { traducir } = parametros;
  return [
    buildResumenSheet(
      traducir, parametros.kpis, parametros.titulo, parametros.subtitulo,
      parametros.periodo, parametros.filtros, parametros.generado,
    ),
    buildDefinicionesSheet(traducir),
    buildEquiposSheet(traducir, parametros.lideres),
  ];
}
